import DOMPurify from 'dompurify'

// NOTE: DOMPurify drops `contenteditable` and `target` by default, and the composer needs both:
// mention and caption-placeholder nodes are atomic units that rely on contenteditable="false",
// and `createTagNode` marks mention links with target="_blank" (alongside rel="noopener").
// Everything else keeps the default policy, which removes event-handler attributes, <script>
// and `javascript:` urls.
const COMPOSER_CONFIG = { ADD_ATTR: ['contenteditable', 'target'] }

// NOTE: DOMPurify has no opinion on `data-*`, so a mention's `data-tag-url` / `data-tag-image`
// survive sanitization untouched. Those values are later reconstituted into a live `href` and
// `img src` (see `createTagNode` and `TagPopup`), which would put a `javascript:` url straight
// back into the DOM. Every url read out of composer html has to go through the helpers below.
const SAFE_LINK_PROTOCOLS = ['http:', 'https:', 'mailto:']
const SAFE_IMAGE_PROTOCOLS = ['http:', 'https:', 'data:', 'blob:']

function sanitizeUrlForProtocols(url: string | undefined | null, protocols: string[]): string {
  if (!url) {
    return ''
  }
  try {
    // NOTE: the base makes relative urls resolvable; absolute urls ignore it
    const { protocol } = new URL(url, window.location.origin)
    return protocols.includes(protocol) ? url : ''
  } catch {
    return ''
  }
}

/**
 * Returns the url if it uses a protocol that is safe to put in an `href`, otherwise an empty
 * string. Blocks `javascript:`, `data:` and every other scripting-capable scheme.
 */
export function sanitizeUrl(url?: string | null): string {
  return sanitizeUrlForProtocols(url, SAFE_LINK_PROTOCOLS)
}

/**
 * Same as `sanitizeUrl`, but also allows `data:` / `blob:` since those are legitimate image
 * sources and are not scriptable when loaded through `<img>`.
 */
export function sanitizeImageUrl(url?: string | null): string {
  return sanitizeUrlForProtocols(url, SAFE_IMAGE_PROTOCOLS)
}

/**
 * Sanitizes composer html before it is written into the DOM.
 *
 * Every `innerHTML` assignment fed with composer html has to go through this. That html is not
 * necessarily something the user typed: it can come from the `shareUrl` / `imageSource` query
 * params, from article links and titles out of third-party feeds, or from posts, comments and
 * drafts reloaded from the API.
 */
export function sanitizeComposerHtml(html: string): string {
  return DOMPurify.sanitize(html, COMPOSER_CONFIG)
}

export default sanitizeComposerHtml
